import React, { useState, useEffect } from "react";
import { FaPlay } from "react-icons/fa";
import { DownloadFile } from "../../Services/Server";
import "./Survey.css";

const SiteDocumentation = ({ surveyData }) => {
	const [siteDocOpen, setSiteDocOpen] = useState(true);
	const [powerSourcesOpen, setPowerSourcesOpen] = useState(false);
	const [routerDescOpen, setRouterDescOpen] = useState(false);

	const [lightbox, setLightbox] = useState(null); // { url, type }

	// Media + loading states
	const [media, setMedia] = useState({
		siteDoc: [],
		siteLocDocs: {},
		powerDocs: {},
		cableDocs: [],
		routerDocs: [],
	});

	const [loading, setLoading] = useState({
		siteDoc: false,
		siteLocDocs: {},
		powerDocs: {},
		cableDocs: false,
		routerDocs: false,
	});

	const fetchMedia = async (ids, section, key = null) => {
		if (!ids || ids.length === 0) return [];
		if (key !== null) {
			setLoading((prev) => ({
				...prev,
				[section]: { ...prev[section], [key]: true },
			}));
		} else {
			setLoading((prev) => ({ ...prev, [section]: true }));
		}

		const files = await Promise.all(
			ids.map(async (id) => {
				try {
					const blob = await DownloadFile(id, true);
					const url = URL.createObjectURL(blob);
					const type = blob.type.startsWith("video/") ? "video" : "image";
					return { id, url, type };
				} catch (e) {
					console.error("Error fetching doc", id, e);
					return null;
				}
			})
		);

		if (key !== null) {
			setLoading((prev) => ({
				...prev,
				[section]: { ...prev[section], [key]: false },
			}));
		} else {
			setLoading((prev) => ({ ...prev, [section]: false }));
		}

		return files.filter(Boolean);
	};

	useEffect(() => {
		if (!surveyData) return;

		const loadAll = async () => {
			const siteDoc = await fetchMedia(surveyData.siteDoc || [], "siteDoc");

			const siteLocDocs = {};
			for (const loc of surveyData.site_loc || []) {
				siteLocDocs[loc.id] = await fetchMedia(
					loc.siteDoc || [],
					"siteLocDocs",
					loc.id
				);
			}

			const powerDocs = {};
			for (const loc of surveyData.site_loc || []) {
				for (const p of loc.power || []) {
					powerDocs[p.id] = await fetchMedia(p.powDoc || [], "powerDocs", p.id);
				}
			}

			const cableDocs = await fetchMedia(
				(surveyData.cable_route || []).flatMap((r) => r.docId || []),
				"cableDocs"
			);

			const routerDocs = await fetchMedia(
				surveyData.router_details?.docid || [],
				"routerDocs"
			);

			setMedia({
				siteDoc,
				siteLocDocs,
				powerDocs,
				cableDocs,
				routerDocs,
			});
		};

		loadAll();
	}, [surveyData]);

	if (!surveyData) return null;

	const {
		ladder,
		scaffload,
		obstruction,
		trunc,
		site_loc = [],
		obstructionDescription,
		pow_back_up,
		cable_length,
		cable_desp,
		router_details = {},
	} = surveyData;

	const siteData = [
		{
			label: "Ladder:",
			value: ladder ? "Yes" : ladder === false ? "No" : "N/A",
		},
		{
			label: "Scaffold:",
			value: scaffload ? "Yes" : scaffload === false ? "No" : "N/A",
		},
		{ label: "Obstructions:", value: obstruction ? "Yes" : "No" },
		{
			label: "Trunking Pipe:",
			value: trunc ? "Yes" : trunc === false ? "No" : "N/A",
		},
		...(site_loc || []).map((loc, index) => ({
			label: index === 0 ? "Primary Location:" : "Secondary Location:",
			value: loc.locationName || `Location ${index + 1}`,
		})),
	];

	const routerData = [
		{
			label: "Where to store router:",
			value: router_details.routerstorage ? "Yes" : "No",
		},
		{
			label: "Drilling/ Wall penetration:",
			value: router_details.penetration ? "Yes" : "No",
		},
	];

	const handleOpenLightbox = (url, type) => setLightbox({ url, type });
	const handleCloseLightbox = () => setLightbox(null);

	const renderMedia = (file, key) => (
		<div
			key={key || file.id}
			className="media-thumbnail"
			onClick={() => handleOpenLightbox(file.url, file.type)}
		>
			{file.type === "video" ? (
				<>
					<video src={file.url} className="thumbnail-video" muted />
					<FaPlay className="play-overlay" />
				</>
			) : (
				<img src={file.url} alt="doc" className="thumbnail-image" />
			)}
		</div>
	);

	return (
		<div className="container">
			{/* SITE DOCUMENTATION */}
			<div className="section">
				<button onClick={() => setSiteDocOpen(!siteDocOpen)} className="header">
					<h2 className="survey-title">Site Documentation</h2>
					<span className={`chevron ${siteDocOpen ? "chevron-rotated" : ""}`}>
						<FaPlay />
					</span>
				</button>

				{siteDocOpen && (
					<div className="content">
						<div className="data-grid">
							{siteData.map((item, index) => (
								<div key={index} className="data-item">
									<span className="data-label">{item.label}</span>
									<span
										className={`data-value ${
											item.value === "Yes"
												? "data-value-yes"
												: item.value === "No"
												? "data-value-no"
												: "data-value-other"
										}`}
									>
										{item.value}
									</span>
								</div>
							))}
						</div>

						{/* Site Locations */}
						<div className="locations-list">
							{site_loc.length ? (
								site_loc.map((loc, index) => (
									<div key={loc.id} className="location-block">
										<h3 className="location-heading">
											{loc.locationName
												? `Location ${index + 1} (${loc.locationName})`
												: `Location ${index + 1}`}
										</h3>
										{loading.siteLocDocs[loc.id] ? (
											<div className="spinner small-spinner" />
										) : media.siteLocDocs[loc.id] &&
										  media.siteLocDocs[loc.id].length > 0 ? (
											<div className="media-gallery">
												{media.siteLocDocs[loc.id].map((file, i) =>
													renderMedia(file, i)
												)}
											</div>
										) : media.siteLocDocs[loc.id] ? (
											<p className="data-value-other">No media available</p>
										) : null}
									</div>
								))
							) : (
								<p className="data-value-other">No locations documented</p>
							)}
						</div>

						<div>
							<h3 className="survey-title">Obstructions Description</h3>
							<p className="obstructions-text">
								{obstructionDescription || "N/A"}
							</p>
						</div>
					</div>
				)}
			</div>

			{/* POWER SOURCES */}
			<div className="section">
				<button
					onClick={() => setPowerSourcesOpen(!powerSourcesOpen)}
					className="header"
				>
					<h2 className="survey-title">Power Sources</h2>
					<span
						className={`chevron ${powerSourcesOpen ? "chevron-rotated" : ""}`}
					>
						<FaPlay />
					</span>
				</button>

				{powerSourcesOpen && (
					<div className="content">
						<div className="data-grid">
							<div className="data-item">
								<span className="data-label">Backup Power Source:</span>
								<span className="data-value-other">{pow_back_up || "N/A"}</span>
							</div>
							<div className="data-item">
								<span className="data-label">
									An Estimate of Cable Run Length:
								</span>
								<span className="data-value-other">
									{cable_length ? `${cable_length} (meters)` : "N/A"}
								</span>
							</div>
						</div>

						<div className="locations-list">
							{site_loc.length ? (
								site_loc.map((loc, index) =>
									loc.power && loc.power.length ? (
										<div key={loc.id} className="location-block">
											<h3 className="location-heading">
												Power Source for {`Location ${index + 1}`}
											</h3>
											{loc.power.map((p) => (
												<div key={p.id} className="power-block">
													<p className="data-value-other">
														Distance: {p.siteLocDis || "N/A"} (meters)
													</p>
													{loading.powerDocs[p.id] ? (
														<div className="spinner small-spinner" />
													) : media.powerDocs[p.id] &&
													  media.powerDocs[p.id].length > 0 ? (
														<div className="media-gallery">
															{media.powerDocs[p.id].map((file, i) =>
																renderMedia(file, i)
															)}
														</div>
													) : media.powerDocs[p.id] ? (
														<p className="data-value-other">
															No media available
														</p>
													) : null}
												</div>
											))}
										</div>
									) : null
								)
							) : (
								<p className="data-value-other">No power sources documented</p>
							)}
						</div>

						<div className="cable-route-section">
							<h3 className="location-title">Cable Route Description</h3>
							<p className="obstructions-text mb">{cable_desp || "N/A"}</p>
						</div>

						<div className="planned-route-section">
							<h3 className="location-title">Planned Cable Route</h3>
							{loading.cableDocs ? (
								<div className="spinner small-spinner" />
							) : media.cableDocs && media.cableDocs.length > 0 ? (
								<div className="media-gallery">
									{media.cableDocs.map((file, i) => renderMedia(file, i))}
								</div>
							) : media.cableDocs ? (
								<p className="data-value-other">
									No planned cable route available
								</p>
							) : null}
						</div>
					</div>
				)}
			</div>

			{/* ROUTER DESCRIPTION */}
			<div className="section">
				<button
					onClick={() => setRouterDescOpen(!routerDescOpen)}
					className="header"
				>
					<h2 className="survey-title">Router Description</h2>
					<span
						className={`chevron ${routerDescOpen ? "chevron-rotated" : ""}`}
					>
						<FaPlay />
					</span>
				</button>

				{routerDescOpen && (
					<div className="content">
						<div className="data-grid">
							{routerData.map((item, index) => (
								<div key={index} className="data-item">
									<span className="data-label">{item.label}</span>
									<span className="data-value-other">{item.value}</span>
								</div>
							))}
						</div>

						<div className="router-description-section">
							<h3 className="location-title">Router Location Description</h3>
							<p className="router-description-text mb">
								{router_details.description || "N/A"}
							</p>
						</div>

						<div className="router-location-section">
							<h3 className="location-title">Router Location</h3>
							{loading.routerDocs ? (
								<div className="spinner small-spinner" />
							) : media.routerDocs && media.routerDocs.length > 0 ? (
								<div className="media-gallery">
									{media.routerDocs.map((file, i) => renderMedia(file, i))}
								</div>
							) : media.routerDocs ? (
								<p className="data-value-other">No media available</p>
							) : null}
						</div>
					</div>
				)}
			</div>

			{lightbox && (
				<div className="lightbox-overlay" onClick={handleCloseLightbox}>
					<div
						className="lightbox-content"
						onClick={(e) => e.stopPropagation()}
					>
						{lightbox.type === "image" ? (
							<img
								src={lightbox.url}
								alt="Full view"
								className="lightbox-image"
							/>
						) : (
							<div className="lightbox-video-wrapper">
								<video
									src={lightbox.url}
									controls
									autoPlay
									className="lightbox-video"
								/>
								<FaPlay className="lightbox-play-overlay" />
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default SiteDocumentation;
